<template lang="pug">
  #member-device.has-text-grey
    WtTitle(h3) Connect A Device

    .columns
      .connect-device-container.column
        .connect-device-layout(v-if="!deviceText")
          p.mb-5 Track your steps automatically by syncing your activity tracking device.

        .edit-device-layout(v-if="deviceText")
          .columns.is-mobile
            .column.is-narrow
              BIcon(type="is-success" icon="check" size="is-large")
            .column
              .is-size-1 Congratulations!
              .subtitle Device connected: {{ deviceText }}
              br
              .info-text
                p
                  span Your device will begin syncing automatically from your connection date and onward.
                p(v-if="lastSync")
                  strong Last Synced: {{ lastSync }}

                div.columns.device-tool-tray
                  .column
                    .sync-button(v-if="showSyncButton")
                        WtButton(
                          @click.native="sync"
                          :loading="isButtonLoading"
                          :disabled="isButtonLoading"
                          color="primary"
                          bold
                        ) SYNC DEVICE NOW

                        p.is-size-7 Your steps are automatically syncing.
                        p.is-size-7 Click to re-sync up to 14 days of step data.

                  .column
                    .edit-connected-device-button
                      WtButton(
                        @click.native="disconnect"
                        :loading="isButtonLoading"
                        :disabled="isButtonLoading"
                        color="red"
                        bold
                      ) DISCONNECT DEVICE

    .horizontal-divider

    .connect-device-layout-footer
      p.mb-0
        span We support the following devices:
        br
        br
        span  Fitbit,
        span  Garmin,
        span  Samsung Health (Android Only),
        span  Apple Health (iOS Only)
        span  and
        span  Health Connect.
        br
        br
        span  Download our mobile app to connect.
        br
        br
        a(
          href="https://support-walkertracker-new.helpscoutdocs.com/article/459-how-syncing-works-with-walker-tracker"
          target="_blank"
        ) Learn more about how Be Well syncs.
        br
        br

        .columns
          .column.is-half.is-offset-one-quarter
            a.app-store-buttons.apple-app-store(
              href="https://itunes.apple.com/us/app/walker-tracker/id844623241?mt=8"
              target="_blank"
            )
              img(
                alt="Download from the App Store"
                src="https://cdn.tbybewell.com/images/app-store.png"
              )

            a.app-store-buttons.google-play-store(
              href="https://play.google.com/store/apps/details?id=com.walkertracker"
              target="_blank"
            )
              img(
                alt="Download from the Google Play Store"
                src="https://cdn.tbybewell.com/images/google-play.png"
              )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "MemberDevice",
  components: { WtTitle, WtTile, WtButton },
  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  data() {
    return {
      isButtonLoading: false,
      apis: {
        fitbit: "fitbit",
        garmin: "garmin",
        googlefit: "googlefit",
        healthConnect: "healthconnect",
        healthkit: "healthkit",
        samsunghealth: "samsunghealth"
      }
    };
  },

  computed: {
    ...mapGetters("members", ["member", "memberLoaded"]),

    deviceText() {
      let deviceApi = this._.get(this.member, "deviceApi", "");

      deviceApi = deviceApi.toString().toLowerCase();

      switch (deviceApi) {
        case this.apis.googlefit:
          return "Google Fit";
        case this.apis.healthConnect:
          return "Apple Health Connect";
        case this.apis.healthkit:
          return "Apple Health";
        case this.apis.garmin:
          return "Garmin";
        case this.apis.samsunghealth:
          return "Samsung Health";
        case this.apis.fitbit:
          return this._.get(this.member, "externalDevice.deviceName", "Fitbit");
        default:
          return "";
      }
    },

    showSyncButton() {
      return this.member.deviceApi === this.apis.fitbit;
    },

    deviceApi() {
      if (this.memberLoaded && this.member.deviceApi) {
        return this.member.deviceApi.toString().toLowerCase();
      }

      return "";
    },

    lastSync() {
      let deviceLastContact = this._.get(
        this.member,
        "externalDevice.deviceLastContact",
        null
      );
      return !this._.isEmpty(deviceLastContact)
        ? this.date(deviceLastContact, "DATE_TIME_FORMAT").formatted
        : deviceLastContact;
    }
  },

  watch: {
    memberLoaded() {
      if (this.memberLoaded) {
        this.isButtonLoading = false;
      }
    }
  },

  mounted() {
    this.trackFeatureView("settings", "settings_device");
  },

  methods: {
    ...mapActions("members", [
      "fetchMember",
      "disconnectDevice",
      "syncDevice",
      "fetchMember"
    ]),
    ...mapActions(["snackBar"]),

    async sync() {
      this.isButtonLoading = true;

      let response = await this.syncDevice();
      if (this.apiCallSuccess(response.data)) {
        // fetch the member for updated data
        await this.$auth.fetch();
        await this.fetchMember({
          memberId: this.$auth.user().id,
          setMember: true
        });

        this.snackBar({
          type: "is-success",
          duration: 10000,
          queue: false,
          message: "Device successfully synced!"
        });

        this.isButtonLoading = false;
      }
    },

    async disconnect() {
      this.isButtonLoading = true;

      try {
        let response = await this.disconnectDevice();

        if (this.apiCallSuccess(response.data)) {
          await this.snackBar({
            type: "is-success",
            duration: 5000,
            queue: false,
            message: response.data.data.message
          });

          try {
            // fetch the member (now that their api is changed)
            await this.$auth.fetch();
            await this.fetchMember({
              memberId: this.$auth.user().id,
              setMember: true
            });

            this.isButtonLoading = false;
          } catch (error) {
            await this.snackBar({
              type: "is-danger",
              duration: 10000,
              queue: false,
              message: error.response
            });
            this.error = true;
          }
        }
      } catch (error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: error.response
        });
        this.error = true;
      }
    },

    apiCallSuccess(payload) {
      if (!payload.status) {
        payload.errors.forEach(error => {
          this.snackBar({
            duration: 5000,
            type: "is-danger",
            message: error
          });
        });
        return false;
      }
      return true;
    }
  }
};
</script>

<style lang="sass" scoped>
#member-device
  .device-tool-tray
    margin-top: 10px
  .connect-device-container
    +mobile
      min-height: 200px
    .connect-device-layout, .edit-device-layout
      min-height: 100%
      height: 100%
      display: flex
      flex-direction: column
      justify-content: space-between
  .connect-button
    width: 75%

  .vertical-divider
    margin-right: 1.5rem
  .horizontal-divider
    margin-bottom: 1.5rem
  ul
    list-style: disc
    margin: 1rem 0 1rem 2rem

  .app-store-buttons
    height: 30px
    border-radius: $radius
    cursor: pointer
    margin: 1rem 1rem 0 0
    overflow: hidden
    justify-content: center
    align-items: center
    &:hover
      box-shadow: $box-shadow-slight
    img
      width: 150px !important
      height: 42px !important

  .connect-device-layout-footer
    text-align: center
    .column
      text-align: center
  .card-body
    text-align: center
</style>
